import { useEffect, useState, type FormEvent } from 'react';
import { AlertTriangle, Lock, LogOut } from 'lucide-react';
import { getPendingCountTotal, useVault } from '../lib/vault';

/**
 * Master Code prompt — shown by `<IdentityGate>` whenever the user has a
 * keypair and vault membership but no identity keys in memory.
 */
export function IdentityUnlockScreen() {
  const [masterCode, setMasterCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const user = useVault((s) => s.user);
  const unlock = useVault((s) => s.unlock);
  const signOut = useVault((s) => s.signOut);
  const [pendingCount, setPendingCount] = useState<number>(0);

  // Poll the registered pending-count providers — the count can change
  // while the unlock screen is up if a peer's PUT lands and pushes the
  // local pendingSync row's flag (rare but possible during a partial
  // sync). Every 3 s is plenty.
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      getPendingCountTotal()
        .then((n) => {
          if (!cancelled) setPendingCount(n);
        })
        .catch((err) => {
          // The provider should never throw, but if one does we don't
          // want to block the unlock screen — log and carry on.
          console.warn('[unlock] pending-count provider failed', err);
        });
    };
    tick();
    const id = setInterval(tick, 3_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await unlock(masterCode);
    } catch (err: any) {
      setError(err?.message ?? 'Unable to unlock.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card w-full max-w-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="text-dragon-500" size={24} />
          <div>
            <h1 className="text-lg font-semibold">Unlock your account</h1>
            <p className="text-xs text-ink-400">
              Hi {user?.username}, enter your Master Code to continue.
            </p>
          </div>
        </div>
        {pendingCount > 0 && (
          <div className="mb-3 flex items-start gap-2 rounded border border-amber-700 bg-amber-950/40 p-2 text-sm text-amber-200">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
            <div>
              <strong>
                {pendingCount === 1
                  ? '1 unsaved edit'
                  : `${pendingCount} unsaved edits`}
              </strong>{' '}
              waiting to sync. Unlock to resume.
            </div>
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-3" autoComplete="off">
          <input
            type="password"
            className="input"
            value={masterCode}
            onChange={(e) => setMasterCode(e.target.value)}
            autoFocus
            placeholder="Master Code"
            required
            name="dragonhq-master-code"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            data-bwignore
            data-dashlane-ignore="true"
            data-form-type="other"
          />
          {error && (
            <div className="rounded border border-red-900 bg-red-950/40 p-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? 'Unlocking…' : 'Unlock'}
          </button>
        </form>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={signOut}
            className="btn-ghost flex-1 text-xs"
          >
            <LogOut size={14} className="mr-1" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
